// Custom handling for the different types of embeds we support.
class EmbedType {
  static title: string;
  static regex: RegExp;
  static iframeAttributes: Record<string, any>;
  static proportionalResize: boolean = true;

  static matches(url: string): boolean {
    return this.regex.test(url);
  }

  static getIframeAttributes(url: string): Record<string, any> {
    const matches = url.match(this.regex);
    if (!matches) {
      return {};
    }

    // Substitute the URL into the iframe src attribute.
    const localAttributes = { ...this.iframeAttributes };
    for (const key in localAttributes) {
      if (typeof localAttributes[key] === 'string') {
        localAttributes[key] = localAttributes[key].replace(
          /%\{(\w+)\}/g,
          (_, replacementKey) => {
            if (replacementKey === 'url') {
              return url;
            } else {
              const index = parseInt(replacementKey, 10);
              if (index >= 1 && index < matches.length) {
                return matches[index];
              }
            }

            return replacementKey;
          }
        );
      }
    }

    return localAttributes;
  }

  static getProportionalResize(): boolean {
    return this.proportionalResize;
  }
}

export class EmbedYouTube extends EmbedType {
  static title = 'YouTube';
  static regex =
    /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;

  static iframeAttributes = {
    src: 'https://www.youtube.com/embed/%{1}',
    allow:
      'accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share',
    allowFullScreen: true,
  };
}

export class EmbedVimeo extends EmbedType {
  static title = 'Vimeo';
  static regex = /^https?:\/\/(?:www\.|player\.)?vimeo\.com(?:\/video)?\/(\d+)/;

  static iframeAttributes = {
    src: 'https://player.vimeo.com/video/%{1}',
    allow: 'encrypted-media',
    allowFullScreen: true,
  };
}
export class EmbedWistia extends EmbedType {
  static title = 'Wistia';
  static regex = /^https?:\/\/(?:.*).wistia\.com\/medias\/(\w+)/;

  static iframeAttributes = {
    src: 'https://fast.wistia.net/embed/iframe/%{1}',
    allow: 'encrypted-media',
    allowFullScreen: true,
  };
}

// https://www.loom.com/share/3a4f548e6afa4a49b8db616c1a61d353?sid=cb1d52ae-1414-4e68-9ea8-d76991987193
// Params available: https://support.loom.com/hc/en-us/articles/360002208317-How-to-embed-your-video-into-a-webpage
export class EmbedLoom extends EmbedType {
  static title = 'Loom';
  static regex = /^https?:\/\/(?:.*).loom\.com\/(?:share|embed)\/(\w+)/;

  static iframeAttributes = {
    src: 'https://www.loom.com/embed/%{1}?hide_share=true&hideEmbedTopBar=true',
    allow: 'encrypted-media',
    allowFullScreen: true,
  };
}

// https://www.figma.com/file/LKQ4FJ4bTnCSjedbRpk931/Sample-File
export class EmbedFigma extends EmbedType {
  static title = 'Figma';
  static regex =
    /https:\/\/[\w.-]+\.?figma.com\/([\w-]+)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/;

  static iframeAttributes = {
    src: 'https://www.figma.com/embed?embed_host=www.aha.io&url=%{url}',
    allowFullScreen: true,
  };

  static proportionalResize = false;
}

// https://gist.github.com/percyhanna/fbf8bfcae8676917fa31f1f030af1953
class EmbedGist extends EmbedType {
  static title = 'GitHub Gist';
  static regex = /https:\/\/gist\.github\.com\/([\w-]+)\/([\w-]+)/;

  static iframeAttributes = {
    src: 'https://gist.github.com/%{1}/%{2}.pibb?scroll=true',
  };

  static proportionalResize = false;
}

// Container for common embed functionality.
class EmbedCommon {
  static embedTypes = [
    EmbedYouTube,
    EmbedFigma,
    EmbedVimeo,
    EmbedWistia,
    EmbedLoom,
    EmbedGist,
  ];

  // Check which embed type our URL matches and return the corresponding EmbedType.
  static embedTypeForUrl(url: string): typeof EmbedType | undefined {
    return this.embedTypes.find((embedType) => embedType.matches(url));
  }
}

export default EmbedCommon;
